import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Mail, Lock, AlertCircle } from 'lucide-react'
import { useLogin, useTelegramLogin } from '../api/auth'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'

const loginSchema = z.object({
  email: z.string().email('Введите корректный email'),
  password: z.string().min(6, 'Минимум 6 символов'),
})

type LoginFormData = z.infer<typeof loginSchema>

// Floating particles component for login page
function LoginParticles({ count = 20 }: { count?: number }) {
  const particles = useMemo(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      delay: `${Math.random() * 20}s`,
      duration: `${15 + Math.random() * 15}s`,
      size: `${1 + Math.random() * 2}px`,
      opacity: 0.1 + Math.random() * 0.3,
    }))
  }, [count])

  return (
    <div className="fixed inset-0 z-[2] pointer-events-none overflow-hidden">
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute rounded-full animate-float"
          style={{
            left: particle.left,
            width: particle.size,
            height: particle.size,
            background: 'rgba(255, 255, 255, 0.3)',
            animationDelay: particle.delay,
            animationDuration: particle.duration,
            opacity: particle.opacity,
          }}
        />
      ))}
    </div>
  )
}

// Telegram Login Widget component
function TelegramLoginButton({
  onAuth,
  isLoading,
}: {
  onAuth: (data: Record<string, string | number>) => void
  isLoading: boolean
}) {
  const containerRef = useRef<HTMLDivElement>(null)

  const handleTelegramAuth = useCallback(
    (user: Record<string, string | number>) => {
      onAuth(user)
    },
    [onAuth]
  )

  useEffect(() => {
    // Make callback available globally for Telegram widget
    ;(window as any).onTelegramAuth = handleTelegramAuth

    const script = document.createElement('script')
    script.src = 'https://telegram.org/js/telegram-widget.js?22'
    script.setAttribute('data-telegram-login', 'eleventhirtystudiobot')
    script.setAttribute('data-size', 'large')
    script.setAttribute('data-radius', '8')
    script.setAttribute('data-onauth', 'onTelegramAuth(user)')
    script.setAttribute('data-request-access', 'write')
    script.async = true

    if (containerRef.current) {
      containerRef.current.innerHTML = ''
      containerRef.current.appendChild(script)
    }

    return () => {
      delete (window as any).onTelegramAuth
    }
  }, [handleTelegramAuth])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-3">
        <div className="animate-spin w-5 h-5 border-2 border-accent-lime border-t-transparent rounded-full" />
        <span className="ml-2 text-sm text-text-secondary">Вход через Telegram...</span>
      </div>
    )
  }

  return <div ref={containerRef} className="flex justify-center" />
}

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [error, setError] = useState<string | null>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/'

  const loginMutation = useLogin()
  const telegramLogin = useTelegramLogin()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  // Parallax effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!imgRef.current) return

      const x = (e.clientX / window.innerWidth - 0.5) * 20
      const y = (e.clientY / window.innerHeight - 0.5) * 20

      imgRef.current.style.transform = `scale(1.1) translate(${x}px, ${y}px)`
    }

    document.addEventListener('mousemove', handleMouseMove)
    return () => document.removeEventListener('mousemove', handleMouseMove)
  }, [])

  const onSubmit = async (data: LoginFormData) => {
    setError(null)
    try {
      await loginMutation.mutateAsync(data)
      navigate(from, { replace: true })
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message || 'Ошибка входа')
      } else {
        setError('Ошибка входа')
      }
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center overflow-hidden">
      {/* Parallax Background */}
      <div className="fixed inset-0 -top-8 -left-8 -right-8 -bottom-8 z-0 overflow-hidden">
        <picture className="block w-full h-full">
          <source
            media="(max-width: 600px)"
            srcSet="https://images.unsplash.com/photo-1519681393784-d120267933ba?w=800&q=70"
          />
          <source
            media="(max-width: 1200px)"
            srcSet="https://images.unsplash.com/photo-1519681393784-d120267933ba?w=1200&q=75"
          />
          <img
            ref={imgRef}
            src="https://images.unsplash.com/photo-1519681393784-d120267933ba?w=1920&q=80"
            alt=""
            className="w-[calc(100%+60px)] h-[calc(100%+60px)] object-cover scale-110 transition-transform duration-300 ease-out"
            style={{ filter: 'brightness(0.25) saturate(0.7)' }}
            loading="eager"
          />
        </picture>
      </div>

      {/* Gradient Overlay */}
      <div
        className="fixed inset-0 z-[1] pointer-events-none"
        style={{
          background: `linear-gradient(
            180deg,
            rgba(10, 10, 10, 0.7) 0%,
            rgba(10, 10, 10, 0.4) 50%,
            rgba(10, 10, 10, 0.8) 100%
          )`
        }}
      />

      {/* Floating Particles */}
      <LoginParticles count={20} />

      <div className="w-full max-w-md px-4 relative z-10">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8 animate-in delay-1" style={{ opacity: 0 }}>
          <div className="text-2xl font-bold tracking-wider text-white">
            11:30 <span className="font-light opacity-50">5TUD10</span>
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex items-center justify-center gap-2.5 mb-8 animate-in delay-2" style={{ opacity: 0 }}>
          <span className="status-dot" />
          <span className="text-xs font-medium tracking-widest uppercase text-text-secondary">
            Система работает
          </span>
        </div>

        {/* Login Card */}
        <div className="glass-card rounded-xl p-8 animate-in delay-3" style={{ opacity: 0 }}>
          <div className="text-center mb-6">
            <h1 className="text-xl font-semibold text-white mb-2">
              Вход в CRM
            </h1>
            <p className="text-sm text-text-secondary">
              Введите данные для входа в систему
            </p>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-md text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              {...register('email')}
              type="email"
              label="Email"
              placeholder="user@1130.studio"
              error={errors.email?.message}
              icon={<Mail className="w-5 h-5" />}
              autoComplete="email"
            />

            <Input
              {...register('password')}
              type="password"
              label="Пароль"
              placeholder="••••••••"
              error={errors.password?.message}
              icon={<Lock className="w-5 h-5" />}
              autoComplete="current-password"
            />

            <Button
              type="submit"
              className="w-full"
              size="lg"
              loading={loginMutation.isPending}
            >
              Войти
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 bg-transparent text-text-tertiary">или</span>
            </div>
          </div>

          {/* Telegram login */}
          <TelegramLoginButton
            onAuth={async (data) => {
              setError(null)
              try {
                await telegramLogin.mutateAsync(data)
                navigate(from, { replace: true })
              } catch (err) {
                if (err instanceof Error) {
                  setError(err.message || 'Ошибка входа через Telegram')
                } else {
                  setError('Ошибка входа через Telegram')
                }
              }
            }}
            isLoading={telegramLogin.isPending}
          />
        </div>

        <p className="text-center text-text-tertiary text-xs mt-6 animate-in delay-4" style={{ opacity: 0 }}>
          CRM для управления проектами студии
        </p>
      </div>
    </div>
  )
}
